import { JsonForms } from '@homex/se-react-components';
import { CarouselItemId, NavButtonType } from '@homex/se-widget-flow-types';
import { JsonFormsCore } from '@jsonforms/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { setCurrentCarouselItem } from 'actions';
import { setCustomFormData } from 'actions/setCustomFormData';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { useAutoAdvanceEffects } from 'hooks/useAutoAdvanceEffects';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  ICarouselItemCustomFormBottomProps,
  IState,
} from 'typings';
import { resolveSeDestination } from 'utils/resolveSeDestination';
import { validateCustomForm } from 'utils/validateCustomForm';

// TODO(CUSTOM): VERIFY THESE STYLES
import { Buttons, Container, FormGroup } from './styles';

export const CAROUSEL_ITEM_ID = CarouselItemId.CustomForm;

export const CarouselItemCustomFormBottom = ({
  carouselItem,
}: ICarouselItemCustomFormBottomProps) => {
  const dispatch = useThunkDispatch();
  const { customForms, flow } = useSelector((state: IState) => ({
    customForms: state.userInput.customForms,
    flow: state.client.flow,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoAdvanceAllowed, setIsAutoAdvanceAllowed] = useState(false);

  const interactedRef = useRef(false);

  const { options } = carouselItem;
  const { destinations, schema, uischema } = options;
  const { properties, seDestination, seValidateData } = schema;

  useEffect(() => {
    interactedRef.current = false;
    let shouldAutoAdvance = false;

    if (properties) {
      for (const key of Object.keys(properties)) {
        const currentProperty = properties[key];

        // No aa for forms with a string or anyOf control
        if (checkIfString(currentProperty) || checkIfAnyOf(currentProperty)) {
          shouldAutoAdvance = false;

          return;
        }

        // Atleast one an aa supported interactive control required
        if (checkIfOneOf(currentProperty) || checkIfBoolean(currentProperty)) {
          shouldAutoAdvance = true;
        }
      }

      setIsAutoAdvanceAllowed(shouldAutoAdvance);
    }
  }, [properties]);

  const handleChange = (state: Pick<JsonFormsCore, 'data' | 'errors'>) => {
    interactedRef.current = true;
    dispatch(
      setCustomFormData({ carouselItemId: carouselItem.id, data: state.data }),
    );
  };

  const enableContinue = validateCustomForm(
    seValidateData,
    customForms[carouselItem.id],
  );

  const destinationItemId =
    destinations[
      resolveSeDestination(seDestination || [], customForms[carouselItem.id])
    ] || destinations.next;

  useAutoAdvanceEffects(
    enableContinue && isAutoAdvanceAllowed && interactedRef.current,
    useCallback(() => {
      setIsLoading(true);
    }, [setIsLoading]),
    useCallback(() => {
      setIsLoading(false);
      dispatch(
        setCurrentCarouselItem(CarouselReduxReducer.SERVICE, destinationItemId),
      );
    }, [destinationItemId, dispatch]),
  );

  return (
    <FormGroup>
      <Container>
        <JsonForms
          data={customForms[carouselItem.id]}
          onChange={handleChange}
          schema={schema}
          uischema={uischema}
        />
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={flow?.buttonType ?? NavButtonType.Default}
          carouselItemId={CAROUSEL_ITEM_ID}
          destinationItemId={destinationItemId}
          isDisabled={!enableContinue}
          isLoading={isLoading}
        />
      </Buttons>
    </FormGroup>
  );
};

const checkIfString = (schema: any) =>
  schema && schema?.type === 'string' && !schema.anyOf && !schema.oneOf;

const checkIfAnyOf = (schema: any) =>
  schema && schema?.type === 'string' && schema.anyOf;

const checkIfOneOf = (schema: any) =>
  schema && schema?.type === 'string' && schema.oneOf;

const checkIfBoolean = (schema: any) => schema && schema?.type === 'boolean';
